import { useCallback, useEffect, useState } from "react";
import { RegisterCodeMetaForm } from "./form/RegisterCodeMetaForm";
import { guid } from "@/lib/stringUtils";
import { getPlayerSyncStatus, registerCode } from "@/components/ds/lib/dsApi";
import { QRCodeSVG } from "qrcode.react";
import { CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, KeyboardIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LoadingSmall } from "@/components/LoadingSmall";

interface Props {
  onRegistered: (code: string) => void;
}

export const DSPageRegisterFlow: React.FC<Props> = ({ onRegistered }) => {
  const { t } = useTranslation();
  const [showManual, setShowManual] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [tempSessionCode, setTempSessionCode] = useState<string>();

  const syncPeriodically = useCallback(() => {
    getPlayerSyncStatus(tempSessionCode).then((response) => {
      if (response.data) {
        setTempSessionCode(response.data.tempSessionCode);
        if (response.data.status === "synced" && response.data.code) {
          handleRegister({ code: response.data.code });
        }
      }
    });
  }, [tempSessionCode]);

  useEffect(() => {
    // Initial call
    syncPeriodically();

    // Set up periodic calls every 5 seconds
    const timeoutId = setInterval(() => {
      syncPeriodically();
    }, 3000);

    // Cleanup function to clear the interval when component unmounts
    return () => clearInterval(timeoutId);
  }, [syncPeriodically]);

  // Function to handle registration
  const handleRegister = async (values: { code: string }) => {
    setIsRegistering(true);
    try {
      const uniqueId = guid();
      const response = await registerCode({
        uniqueId,
        code: values.code,
        deviceName: "TestDeviceName",
      });

      if (response.data.isSuccess) {
        localStorage.setItem("UniqueId", uniqueId);
        localStorage.setItem("Code", values.code);
        onRegistered(values.code);
      } else {
        alert(`Error: ${response.data.data}`);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsRegistering(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 py-12 min-h-[80vh] flex items-center">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 w-full">
          {/* Left side - QR Code */}
          <div className="order-1 md:order-1">
            <CardContent className="flex flex-col p-8 space-y-6">
              <h1 className="text-4xl font-bold tracking-tight text-gray-900">{t("playerRegister.title")}</h1>
              <p className="text-lg text-gray-600">{t("playerRegister.scanWithPhone")}</p>
              {tempSessionCode ? (
                <QRCodeSVG
                  value={`com.corevo.mobile://playerAddViaQrPreFill?tempSessionCode=${tempSessionCode}`}
                  size={400}
                  level="H"
                  className="w-full h-auto max-w-[400px]"
                  imageSettings={{
                    src: "/logo.png",
                    height: 32,
                    width: 100,
                    excavate: true,
                  }}
                />
              ) : (
                <div className="w-[400px] h-[400px] flex items-center justify-center">
                  <LoadingSmall />
                </div>
              )}
            </CardContent>
          </div>

          {/* Right side - Title and Manual Input */}
          <div className="order-2 md:order-2 space-y-6 p-8">
            <div className="space-y-4">
              <h1 className="text-xl font-medium tracking-tight text-gray-900">{t("playerRegister.manualTitle")}</h1>
              <p className="text-md text-gray-600 leading-relaxed">{t("playerRegister.description")}</p>
            </div>

            {!showManual ? (
              <div className="space-y-6">
                <Button
                  variant="outline"
                  className="py-6 text-lg hover:bg-gray-50 transition-all duration-300"
                  onClick={() => setShowManual(true)}
                >
                  <KeyboardIcon className="h-5 w-5 mr-2" />
                  {t("playerRegister.manual")}
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                <Button variant="ghost" className="mb-6" onClick={() => setShowManual(false)}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t("playerRegister.backToQr")}
                </Button>
                <RegisterCodeMetaForm onSubmit={handleRegister} isLoading={isRegistering} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
